import React, { useState } from 'react';
import { Form, Input, Button, notification, Select, Upload, Tabs, Typography, Radio } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { createDocument } from "../../redux/api/documents/documentsThunks";
import { selectDocumentTypes, selectPropertyTypes } from "../../redux/api/documents/documentsSelectors";
import { CreateDocumentRequest, PropertyType } from "../../types";
import { postData } from "../../services/data-service";
import { DOCUMENTS_UPLOAD_ENDPOINT } from "../../services/utils/api-consts";
import { setOperation } from "../../redux/api/operations/operationsSlice";
import { pollOperationStatus } from "../../redux/api/operations/operationsThunks";

const { Dragger } = Upload;
const { Text } = Typography;

// ─── helpers ──────────────────────────────────────────────────────────────────

const parseStringToList = (input?: string): string[] => {
    if (!input) return [];
    return input.split(';').map(s => s.trim()).filter(Boolean);
};

// ─── Вкладка 1: загрузка файла ────────────────────────────────────────────────

const UploadTab: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const documentTypes = useAppSelector(selectDocumentTypes);
    const currentSet = useAppSelector((s) => s.documentSet.currentSet);

    const handleSubmit = async (values: any) => {
        const fileList = values.file?.fileList ?? [];
        if (fileList.length === 0) {
            notification.error({ message: 'Выберите файл для загрузки' });
            return;
        }

        const selectedDocType = documentTypes.find(dt => dt.code === values.documentTypeCode);
        if (!selectedDocType) {
            notification.error({ message: 'Выберите тип документа' });
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', fileList[0].originFileObj);
            formData.append('documentTypeId', selectedDocType.id);
            formData.append('documentKind', selectedDocType.code);
            formData.append('title', values.title || selectedDocType.name);
            formData.append('setType', values.setType ?? currentSet);
            if (values.description) formData.append('description', values.description);

            const response = await postData<any, FormData>(DOCUMENTS_UPLOAD_ENDPOINT, formData);

            if (response.operationId) {
                dispatch(setOperation({
                    id: response.operationId,
                    type: 'CREATE_DOCUMENT',
                    status: 'InProgress',
                    progress: 0,
                    errorMessage: null,
                    createdAt: new Date().toISOString(),
                    completedAt: null,
                    meta: { documentId: response.documentId },
                }));
                dispatch(pollOperationStatus(response.operationId));
            }

            notification.success({ message: 'Файл загружен, начата обработка' });
            form.resetFields();
        } catch (error: any) {
            notification.error({
                message: 'Ошибка загрузки файла',
                description: error?.response?.data?.message || error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            initialValues={{ setType: currentSet }}
        >
            <Form.Item name="setType" label="Добавить в набор">
                <Radio.Group>
                    <Radio value="shared">Общий</Radio>
                    <Radio value="personal">Личный</Radio>
                </Radio.Group>
            </Form.Item>

            <Form.Item
                name="documentTypeCode"
                label="Тип документа"
                rules={[{ required: true, message: 'Выберите тип документа' }]}
            >
                <Select placeholder="Выберите тип документа">
                    {documentTypes.map(dt => (
                        <Select.Option key={dt.code} value={dt.code}>{dt.name}</Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item name="title" label="Название (опционально)">
                <Input placeholder="Оставьте пустым — будет взято из типа документа" />
            </Form.Item>

            <Form.Item name="description" label="Описание (опционально)">
                <Input.TextArea rows={2} />
            </Form.Item>

            <Form.Item
                name="file"
                label="Файл документа (PDF / DOCX)"
                rules={[{ required: true, message: 'Прикрепите файл' }]}
            >
                <Dragger beforeUpload={() => false} maxCount={1} accept=".pdf,.docx">
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Нажмите или перетащите файл</p>
                    <p className="ant-upload-hint">
                        PDF или DOCX. После загрузки начнётся автоматическое извлечение данных.
                    </p>
                </Dragger>
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                    Загрузить и запустить обработку
                </Button>
            </Form.Item>
        </Form>
    );
};

// ─── Вкладка 2: ручной ввод ───────────────────────────────────────────────────

const ManualTab: React.FC = () => {
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [propertyTypesState, setPropertyTypesState] = useState<PropertyType[]>([]);
    const [isAddingCustomProperty, setIsAddingCustomProperty] = useState(false);
    const [customPropertySelection, setCustomPropertySelection] = useState<string | null>(null);

    const documentTypes = useAppSelector(selectDocumentTypes);
    const propertyTypes = useAppSelector(selectPropertyTypes);
    const currentSet = useAppSelector((s) => s.documentSet.currentSet);

    const handleSubmit = async (values: any) => {
        if (!values.documentTypeCode) {
            notification.error({ message: 'Выберите тип документа' });
            return;
        }

        const properties = Object.entries(values.propertyInputs || {}).flatMap(([propTypeId, rawText]) =>
            parseStringToList(rawText as string).map(text => ({ propertyTypeId: propTypeId, text }))
        );

        const selectedDocType = documentTypes.find(dt => dt.code === values.documentTypeCode);
        const documentData: CreateDocumentRequest = {
            title: values.title,
            description: values.description,
            documentTypeId: selectedDocType?.id || '',
            properties,
            setType: values.setType ?? currentSet,
        };

        setLoading(true);
        try {
            await dispatch(createDocument(documentData)).unwrap();
            notification.success({ message: 'Документ создан' });
            form.resetFields();
            setPropertyTypesState([]);
        } catch (error: any) {
            notification.error({
                message: 'Ошибка при создании документа',
                description: error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentTypeChange = (value: string) => {
        const selectedDocumentType = documentTypes.find(dt => dt.code === value);
        if (selectedDocumentType) {
            const defaultProperties = propertyTypes.filter(pt =>
                selectedDocumentType.defaultPropertyTypeIds.includes(pt.id)
            );
            setPropertyTypesState(defaultProperties);
            form.setFieldsValue({
                propertyInputs: defaultProperties.reduce((acc, pt) => ({ ...acc, [pt.id]: '' }), {}),
            });
        }
    };

    const handleAddCustomProperty = (propertyTypeId: string) => {
        const newPropertyType = propertyTypes.find(pt => pt.id === propertyTypeId);
        if (newPropertyType && !propertyTypesState.some(pt => pt.id === propertyTypeId)) {
            setPropertyTypesState(prev => [...prev, newPropertyType]);
            form.setFieldsValue({
                propertyInputs: { ...form.getFieldValue('propertyInputs'), [propertyTypeId]: '' },
            });
        }
        setIsAddingCustomProperty(false);
        setCustomPropertySelection(null);
    };

    const availableCustomPropertyOptions = propertyTypes.filter(
        pt => !propertyTypesState.some(e => e.id === pt.id)
    );

    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            initialValues={{ propertyInputs: {}, setType: currentSet }}
        >
            <Form.Item
                name="title"
                label="Название документа"
                rules={[{ required: true, message: 'Введите название документа' }]}
            >
                <Input placeholder="Введите название документа" />
            </Form.Item>

            <Form.Item name="setType" label="Добавить в набор">
                <Radio.Group>
                    <Radio value="shared">Общий</Radio>
                    <Radio value="personal">Личный</Radio>
                </Radio.Group>
            </Form.Item>

            <Form.Item name="description" label="Описание (опционально)">
                <Input.TextArea rows={3} placeholder="Краткое описание" />
            </Form.Item>

            <Form.Item
                name="documentTypeCode"
                label="Тип документа"
                rules={[{ required: true, message: 'Выберите тип документа' }]}
            >
                <Select placeholder="Выберите тип документа" onChange={handleDocumentTypeChange}>
                    {documentTypes.map(dt => (
                        <Select.Option key={dt.code} value={dt.code}>{dt.name}</Select.Option>
                    ))}
                </Select>
            </Form.Item>

            {propertyTypesState.map((propType: PropertyType) => (
                <Form.Item
                    key={propType.id}
                    label={`${propType.name}`}
                    name={['propertyInputs', propType.id]}
                >
                    <Input.TextArea
                        rows={2}
                        placeholder="Значения через точку с запятой: значение1; значение2"
                    />
                </Form.Item>
            ))}

            {isAddingCustomProperty ? (
                <Form.Item label="Выберите тип свойства">
                    <Select
                        placeholder="Тип свойства"
                        value={customPropertySelection || undefined}
                        onChange={setCustomPropertySelection}
                        style={{ width: '100%' }}
                    >
                        {availableCustomPropertyOptions.map(pt => (
                            <Select.Option key={pt.id} value={pt.id}>{pt.name}</Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={() => customPropertySelection && handleAddCustomProperty(customPropertySelection)}
                        style={{ marginTop: 8 }}
                    >
                        Добавить
                    </Button>
                    <Button style={{ marginTop: 8, marginLeft: 8 }} onClick={() => setIsAddingCustomProperty(false)}>
                        Отмена
                    </Button>
                </Form.Item>
            ) : (
                <Form.Item>
                    <Button type="dashed" onClick={() => setIsAddingCustomProperty(true)} block>
                        + Добавить тип свойства
                    </Button>
                </Form.Item>
            )}

            {propertyTypesState.length > 0 && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                    Разделяйте несколько значений одного типа точкой с запятой (;)
                </Text>
            )}

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} block>
                    Создать документ
                </Button>
            </Form.Item>
        </Form>
    );
};

// ─── Основной компонент (табы) ────────────────────────────────────────────────

const NewDocumentSimpleTab: React.FC = () => {
    const tabItems = [
        {
            key: 'upload',
            label: '📄 Загрузить файл',
            children: <UploadTab />,
        },
        {
            key: 'manual',
            label: '✏️ Ввести вручную',
            children: <ManualTab />,
        },
    ];

    return <Tabs defaultActiveKey="upload" items={tabItems} />;
};

export default NewDocumentSimpleTab;
